/**
 * The inline "Edit" control on a planned or proposed row of the Org Planning Overview. Its own
 * file because `OrgChartTable.tsx` sits at the 500-line ceiling.
 */
import { Pencil } from "lucide-react";
import { Button } from "@finn-auto/design-system/atoms/button";
import type { OrgLeaf } from "../../services/internalPlaygroundApi";
import type { EditRoleTarget } from "./EditRoleModal";
import { useViewerScope } from "./viewerScope";

/**
 * What the table needs to offer "Edit" on planned and proposed rows. Everyone who can open the page
 * may edit either kind within 10%; anything bigger goes to the approvers, which the form explains.
 */
export interface RoleEditing {
  onEdit: (target: EditRoleTarget) => void;
}

/** Sized like the `Badge`s on the same row, like `BackfillControl`'s inline button. */
export function EditRoleButton({
  leaf,
  edit,
}: {
  leaf: OrgLeaf;
  edit?: RoleEditing;
}) {
  const scope = useViewerScope();
  if (!edit || leaf.kind === "actual" || !leaf.source || !leaf.recordId)
    return null;
  // A proposer edits only roles that sit under someone in their own reporting line.
  if (!scope.inLine(leaf.managerEmployeeId)) return null;
  const { source, recordId } = leaf;
  return (
    <>
      {leaf.pendingChangeRequestId && (
        // Same hand-rolled pill as `ProposedBadge`, amber: a decision is outstanding on this row.
        <span className="inline-flex shrink-0 items-center rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
          change awaiting approval
        </span>
      )}
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className="!h-auto !min-h-0 shrink-0 !px-1.5 !py-0.5 !text-[10px] !font-medium"
        aria-label={`Edit ${leaf.label}`}
        onClick={(event: React.MouseEvent) => {
          event.stopPropagation();
          edit.onEdit({ source, recordId, label: leaf.label });
        }}
      >
        <Pencil className="h-3 w-3" />
        Edit
      </Button>
    </>
  );
}
